import { PlusOutlined } from "@ant-design/icons"
import { BackButton } from "components/BackButton/BackButton"
import { Button } from "components/Button/Button"
import { Drawer } from "components/Drawer/Drawer"
import { ErrorStatus } from "components/ErrorStatus/ErrorStatus"
import { Form } from "components/Form/Form"
import { useNotification } from "components/Notification/Notification"
import { useDisclosure } from "hooks/useDisclosure"
import { AdminCareOrganizationCreatePayload } from "interface/careOrganization"
import { useCallback } from "react"
import { useAdminCareOrganizationCreate } from "services/organizations"
import { DEFAULT_TIMEZONE, GENERIC_ERROR_MESSAGE } from "utils/constants"
import { CareOrganizationFormFields } from "./CareOrganizationFormFields"

export function CreateCareOrganization() {
	const { notify } = useNotification()
	const disclose = useDisclosure()
	const [form] = Form.useForm<FormValues>()
	const create = useAdminCareOrganizationCreate()
	const title = "Create care organization"
	const handleCancel = useCallback(() => {
		if (create.isPending) return
		disclose.close()
		form.resetFields()
		create.reset()
	}, [create, disclose, form])
	const handleFinish = useCallback(
		(values: FormValues) => {
			const name = values.name.trim()

			create.mutate(
				{ ...values, name },
				{
					onSuccess: () => {
						notify.success({
							message: title,
							description: `${name} was created successfully`,
						})
						disclose.close()
						form.resetFields()
					},
				},
			)
		},
		[create, disclose, form, notify],
	)

	return (
		<>
			<Button
				type="primary"
				size="large"
				icon={<PlusOutlined aria-hidden />}
				onClick={disclose.open}
			>
				Create
			</Button>

			{disclose.isOpen && (
				<Drawer
					open
					aria-label={title}
					title={
						<BackButton disabled={create.isPending} onClick={handleCancel}>
							Care organizations
						</BackButton>
					}
					rootClassName="z-10"
					onClose={handleCancel}
				>
					<h2 className="font-headings text-3xl font-semibold">{title}</h2>
					<Form<FormValues>
						form={form}
						autoComplete="off"
						name="admin-care-organization-create"
						layout="vertical"
						aria-label={title}
						initialValues={INITIAL_VALUES}
						className="overflow-y-auto rounded-2xl bg-mint-0 p-2 xs:p-4"
						onFinish={handleFinish}
					>
						<CareOrganizationFormFields
							fields={["name", "timezone", "excludedFromAnalytics"]}
						/>
						<Form.Item className="flex flex-col gap-2 pt-2">
							<Button
								type="primary"
								htmlType="button"
								loading={create.isPending}
								className="w-full"
								onClick={form.submit}
							>
								Confirm
							</Button>
							<ErrorStatus show={create.isError}>
								{create.error?.message ?? GENERIC_ERROR_MESSAGE}
							</ErrorStatus>
						</Form.Item>
					</Form>
				</Drawer>
			)}
		</>
	)
}

const INITIAL_VALUES: Partial<FormValues> = {
	timezone: DEFAULT_TIMEZONE,
	excludedFromAnalytics: false,
}

type FormValues = Required<AdminCareOrganizationCreatePayload>
